import {
  portalSendBackAnswerKinds,
  shownPortalItems,
  type PortalRequestResponse,
} from "@ledgerbox/domain"

/* What an item row says about itself, and what the page offers about the
   items the firm sent back. Plain logic, so every state is testable. */

type Item = PortalRequestResponse["items"][number]

/** The request as the client sees it: an item an earlier answer skipped is
 * left out, and shows up the moment the answer that asks for it is sent. */
export const withShownItems = (
  request: PortalRequestResponse
): PortalRequestResponse => ({
  ...request,
  items: shownPortalItems(request.items),
})

/** The firm sent the item back and the client has not answered it since:
 * an upload or an answer clears the reason. */
export const isSentBack = (item: Pick<Item, "rejectionReason">): boolean =>
  Boolean(item.rejectionReason?.trim())

/**
 * What a press on a Yes/No or one-answer pill sends. A second press on the
 * held pill takes the answer back, except on a sent-back item: there the
 * firm asked the client to check the answer, and pressing it again says it
 * stands (R3-B2).
 */
export const pillPressValue = <V extends string | boolean>(
  held: unknown,
  pressed: V,
  sentBack: boolean
): V | null => (held === pressed && !sentBack ? null : pressed)

/**
 * A sent-back item that still holds an answer offers "Keep my answer", which
 * sends the same answer again. A Yes/No or one-answer pill does this with a
 * press on the held pill, so it gets no separate button.
 */
export const offersKeepAnswer = (
  item: Pick<Item, "kind" | "allowMultiple" | "rejectionReason"> & {
    answer?: unknown
  },
  sentBack: boolean
): boolean =>
  sentBack &&
  item.answer != null &&
  item.kind !== "yes_no" &&
  !(item.kind === "choice" && !item.allowMultiple) &&
  item.kind !== "document" &&
  item.kind !== "signature" &&
  item.kind !== "transaction_question"

/** An answer whose field has its own Send button: amount, table and text.
 * "Keep my answer" takes that button's place there (R9-U7). */
export const answerHasSendSlot = (
  item: Pick<Item, "kind" | "allowMultiple">
): boolean =>
  item.kind === "amount" || item.kind === "table" || item.kind === "text"

/**
 * A letter the firm canceled, or whose signer the firm removed. It stays
 * open for the firm to replace or remove, but the client has nothing to
 * sign, so it is not the client's task.
 */
export const isCanceledLetter = (
  item: Partial<Pick<Item, "kind" | "signature">>
): boolean => item.kind === "signature" && item.signature?.status === "voided"

/**
 * An answer the client gave that nobody at the firm has looked at. A
 * regular request reads a complete answer as accepted the moment it is
 * sent, but to the client it is only sent: "Accepted" is the firm's word.
 */
const answerNotYetAccepted = (
  item: Partial<Pick<Item, "kind" | "acceptedAt" | "verifiedAt">>
): boolean =>
  item.kind !== undefined &&
  (portalSendBackAnswerKinds as readonly string[]).includes(item.kind) &&
  !item.acceptedAt &&
  !item.verifiedAt

/** The word at the right of a task row. */
export const taskStateLabel = (
  item: Pick<Item, "status" | "required" | "rejectionReason"> &
    Partial<Pick<Item, "kind" | "signature" | "acceptedAt" | "verifiedAt">>
): string =>
  isCanceledLetter(item)
    ? "Canceled"
    : isSentBack(item)
      ? "Sent back"
      : item.status === "accepted" && !answerNotYetAccepted(item)
        ? "Accepted"
        : item.status === "accepted" ||
            item.status === "received" ||
            item.status === "needs_review"
          ? "Sent"
          : item.required
            ? "Needed"
            : "Optional"

/** Still the client's to do: nothing in yet, or sent back. A canceled
 * letter is not. */
export const needsClient = (
  item: Pick<Item, "status" | "rejectionReason"> &
    Partial<Pick<Item, "kind" | "signature">>
): boolean =>
  !isCanceledLetter(item) && (item.status === "open" || isSentBack(item))

/**
 * The required items that still hold up "Send to firm": open ones, less a
 * canceled letter. On an engagement request "needs_review" is a blocker the
 * server also waits on. On any other request it is a file the firm is
 * checking, which the client has sent and cannot do more about (R12-F1).
 */
export const requiredTasksLeft = <
  T extends Pick<Item, "status" | "required"> &
    Partial<Pick<Item, "kind" | "signature">>,
>(
  items: readonly T[],
  engagement: boolean
): T[] =>
  items.filter(
    (item) =>
      item.required &&
      !isCanceledLetter(item) &&
      (item.status === "open" || (engagement && item.status === "needs_review"))
  )

type DoneFacts = Pick<Item, "status"> &
  Partial<Pick<Item, "kind" | "signature" | "rejectionReason" | "required">>

/** Nothing more happens on the item: accepted, signed, a settled question,
 * or a canceled letter. */
export const isDoneTask = (item: DoneFacts): boolean =>
  isCanceledLetter(item) ||
  (item.status === "accepted" &&
    !isSentBack({ rejectionReason: item.rejectionReason ?? null }))

/**
 * Every item the client sees is done. Nothing is left for the client to
 * send, so the page does not ask them to "Send to firm" (R8-F2). An open
 * optional item keeps the button, so the client can still say they have
 * nothing more.
 */
/** "I've sent everything" can be taken back until the firm closes the
 * request, on an engagement request as on any other. */
export const canUndoClientDone = (
  request: Pick<PortalRequestResponse, "clientDoneAt" | "closedAt">
): boolean => Boolean(request.clientDoneAt) && !request.closedAt

export const everythingAccepted = (items: readonly DoneFacts[]): boolean =>
  items.length > 0 && items.every(isDoneTask)

/**
 * The tone of a task row's status dot (R3-U6): "todo" is the client's to do
 * now (a required item not in yet, or one sent back), "done" is finished,
 * "quiet" is the rest (sent and waiting on the firm, optional, canceled).
 */
export const taskStateTone = (item: DoneFacts): "todo" | "done" | "quiet" =>
  isCanceledLetter(item)
    ? "quiet"
    : isSentBack({ rejectionReason: item.rejectionReason ?? null }) ||
        (item.required === true && item.status === "open")
      ? "todo"
      : isDoneTask(item)
        ? "done"
        : "quiet"

/** How many required items are canceled letters, which count as in for
 * "I've sent everything". */
export const canceledRequiredLetters = (
  items: readonly (Pick<Item, "required"> &
    Partial<Pick<Item, "kind" | "signature">>)[]
): number =>
  items.filter((item) => item.required && isCanceledLetter(item)).length

/**
 * The file button on an item row, or null when the row has none. A one-file
 * item that holds a file takes no more, unless the firm sent it back: then
 * the new file replaces the one it holds. A sent-back engagement item reads
 * as open while it still holds the old file, so the reason decides first.
 */
export const addFileLabel = (
  item: Pick<Item, "kind" | "status" | "allowMultiple" | "rejectionReason">,
  uploadAllowed: boolean
): string | null => {
  if (item.kind !== "document" || !uploadAllowed) return null
  if (item.allowMultiple) return "Add files"
  if (isSentBack(item)) return "Send a new file"
  return item.status === "open" ? "Add a file" : null
}

/** The word beside a file the client sent: extracted once the firm is done
    with it, received until then. The status key stays "processed". */
export const fileStatusLabel = (status: string): string =>
  status === "processed" ? "Extracted" : "Received"

/**
 * The word beside a file on an engagement item. On a sent-back one-file
 * item the file it holds is the one the firm sent back, and a new file
 * replaces it, so it does not read as received.
 */
export const heldFileLabel = (
  file: { status: string },
  item: Pick<Item, "allowMultiple" | "rejectionReason">
): string =>
  isSentBack(item) && !item.allowMultiple
    ? "Sent back"
    : fileStatusLabel(file.status)

/**
 * The camera button beside the file button, or null. It shows only on a
 * touch screen, where the phone opens its camera; anywhere else "Add a
 * file" already reaches every photo there is.
 */
export const takePhotoLabel = (
  item: Pick<Item, "kind" | "status" | "allowMultiple" | "rejectionReason">,
  uploadAllowed: boolean,
  coarsePointer: boolean
): string | null =>
  coarsePointer && addFileLabel(item, uploadAllowed) ? "Take a photo" : null

/** A question about transactions: it gets its own section on the task
 * list, apart from what is needed to begin. */
export const isTransactionQuestion = (item: Pick<Item, "kind">): boolean =>
  item.kind === "transaction_question"

/**
 * The task list's three sections, in page order: what is needed to begin,
 * the questions about transactions, then what can wait. A question never
 * holds up the month's documents, so it does not sit with them.
 */
export const taskSections = <T>(
  tasks: readonly T[],
  itemOf: (task: T) => Pick<Item, "kind" | "required">
): { needed: T[]; questions: T[]; later: T[] } => ({
  needed: tasks.filter(
    (task) => !isTransactionQuestion(itemOf(task)) && itemOf(task).required
  ),
  questions: tasks.filter((task) => isTransactionQuestion(itemOf(task))),
  later: tasks.filter(
    (task) => !isTransactionQuestion(itemOf(task)) && !itemOf(task).required
  ),
})

/**
 * Every item the client sees, in the order their page lists them, so the
 * header button walks the sent-back ones top to bottom. The engagement task
 * list puts what is needed to begin first, then the questions, then what
 * can wait.
 */
export const itemsInPageOrder = (
  requests: readonly PortalRequestResponse[],
  engagement: boolean
): Item[] => {
  const items = requests.flatMap((request) => withShownItems(request).items)
  if (!engagement) return items
  const sections = taskSections(items, (item) => item)
  return [...sections.needed, ...sections.questions, ...sections.later]
}

/** "1 item to fix", "3 items to fix". */
export const itemsToFixLabel = (count: number): string =>
  `${count} ${count === 1 ? "item" : "items"} to fix`

/**
 * The item the header button goes to next. `presses` counts the presses so
 * far, so each press moves on and the last one comes round to the first.
 */
export const nextItemToFix = <
  T extends Pick<Item, "itemId" | "rejectionReason">,
>(
  items: readonly T[],
  presses: number
): T | null => {
  const toFix = items.filter(isSentBack)
  return toFix.length ? toFix[presses % toFix.length]! : null
}
